import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

export default function Disclosures() {
  const page = webPageSchema({
    path: "/disclosures",
    title: "Relationships & Financial Disclosure | Shariq Refai, MD, MBA",
    description:
      "Transparent disclosure of Dr. Shariq Refai's ownership interests, founding roles, and the relationships between shariqrefai.com and the ventures he leads.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Disclosures", path: "/disclosures" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Relationships & Financial Disclosure | Shariq Refai, MD, MBA"
        description="Transparent disclosure of Dr. Shariq Refai's ownership interests, founding roles, and the relationships between shariqrefai.com and the ventures he leads."
        url="/disclosures"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Disclosures", path: "/disclosures" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Disclosures</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Relationships &amp; Financial Disclosure
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Who owns what, who founded what, and how editorial independence
            is preserved.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: May 23, 2026.</strong>
          </p>

          <p>
            Shariq Refai, MD, MBA is a physician-author whose work spans
            clinical practice, educational publishing, and self-guided
            wellness programs. He believes readers and patients have the
            right to know which of those relationships exist, and where
            money flows. This page lays it out plainly.
          </p>

          <h2>Roles and Ownership Interests</h2>
          <p>Dr. Refai is:</p>
          <ul>
            <li>
              <strong>Founder and CEO of{" "}
              <a href={withNetworkUtm("https://shrinkmd.com/", "disclosures_page")} target="_blank" rel="noopener noreferrer">
                shrinkMD
              </a></strong>:
             an independent multistate telepsychiatry company. Dr. Refai
              holds a material ownership interest in shrinkMD.
            </li>
            <li>
              <strong>Founder of{" "}
              <a href={withNetworkUtm("https://shrinq.com/", "disclosures_page")} target="_blank" rel="noopener noreferrer">
                shrinQ
              </a></strong>:
             a self-guided digital mental wellness program. Dr. Refai
              holds a material ownership interest in shrinQ.
            </li>
            <li>
              <strong>Creator of the{" "}
              <a href={withNetworkUtm("https://beunstuck.app/", "disclosures_page")} target="_blank" rel="noopener noreferrer">
                Unstuck
              </a>{" "}
              app</strong>: a general mental wellness app. Dr. Refai holds a
              material ownership interest in Unstuck.
            </li>
            <li>
              <strong>Founder of shrinkMD Publishing</strong>: the
              independent imprint that publishes his books, including{" "}
              <em>Your Mind Is Full of Sh*t</em>, the companion workbook,
              and <em>The Havoc in Your Head</em>. Dr. Refai is the author
              of each of these titles and receives publishing income from
              their sale.
            </li>
            <li>
              <strong>Medical reviewer / medical editor</strong> for the
              editorial publications AnxietyResource.org,
              DepressionResource.org, AnxietyResearch.org, PsychiatryRx.org,
              and ShrinkDaily.com, which are part of the broader{" "}
              <a
                href={withNetworkUtm("https://shrinknetwork.com/", "disclosures_page")}
                rel="noopener"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                Shrink Network
              </a>{" "}
              of educational properties.
            </li>
          </ul>
          <p>
            Where Dr. Refai is referenced on this website as a "founder,"
            "creator," "author," or "editor," those relationships are real,
            ongoing, and financial.
          </p>

          <h2>What shariqrefai.com Is and Isn't</h2>
          <p>
            shariqrefai.com is an educational author and media platform. It
            is not a clinical service, not a substitute for clinical care,
            and not the customer-facing surface for any of the products
            above. It exists to publish books, essays, perspectives, and
            speaking and media information.
          </p>
          <p>
            Each of the other ventures (shrinkMD, shrinQ, Unstuck, shrinkMD
            Publishing, and the editorial publications) operates separately
            from this website, with its own terms, privacy policies, and
            scope of service. They are not operated by, affiliated with, or
            services of this educational platform, even though they share a
            founder.
          </p>

          <h2>Editorial Independence</h2>
          <p>
            Editorial content on this website is intended to remain
            independent and evidence-based. Specifically:
          </p>
          <ul>
            <li>No outside sponsors pay to influence what gets published, how it is framed, or what is recommended.</li>
            <li>No pharmaceutical company, device manufacturer, supplement company, or treatment program pays for placement, mention, or endorsement on this site.</li>
            <li>Articles, essays, books, and frameworks may reference Dr. Refai's own ventures (shrinkMD, shrinQ, Unstuck, shrinkMD Publishing) where directly relevant to the topic, with the relationship disclosed at or near the reference.</li>
            <li>References to other people's books, tools, or services are made on editorial merit, not for compensation.</li>
          </ul>

          <h2>Promotional vs. Editorial Content</h2>
          <p>
            Pages that describe Dr. Refai's own ventures (for example, the{" "}
            <Link href="/projects">Projects &amp; Initiatives</Link> page,
            the homepage Founder Projects section, and bios across the site)
            are clearly identified as descriptions of his ventures, not
            independent third-party recommendations. Each such reference
            carries a relationship statement.
          </p>

          <h2>External Sponsorships, Honoraria, and Speaking Fees</h2>
          <p>
            Dr. Refai may from time to time accept honoraria, speaking
            fees, advisory roles, or consulting engagements. Where any such
            relationship is materially relevant to a topic he writes about
            on this site, it will be disclosed on or near the relevant
            content. If a meaningful new external relationship is
            established, this page will be updated.
          </p>

          <h2>Books and Royalties</h2>
          <p>
            Dr. Refai is the author of, and receives royalties from, the
            books published through shrinkMD Publishing. Discussions of
            those books on this website (excerpts, summaries, related
            essays) are promotional in the ordinary sense that they aim to
            describe and share the work. Buying a book results in
            compensation to Dr. Refai.
          </p>

          <h2>Affiliate Links</h2>
          <p>
            At the time of this update, shariqrefai.com does not participate
            in any affiliate program and does not earn referral commissions
            from external links. If that ever changes, affiliate
            relationships will be disclosed near the relevant links and
            globally on this page.
          </p>

          <h2>Insurance, Reimbursement, and Patient Care</h2>
          <p>
            This website does not provide patient care, does not bill
            insurance, and does not handle reimbursement. All clinical
            services, when offered, are provided through shrinkMD under its
            own terms and policies.
          </p>

          <h2>Conflicts and Corrections</h2>
          <p>
            If you believe a piece of content on this site reflects an
            undisclosed conflict of interest, or if you spot a factual
            issue, please flag it via the{" "}
            <Link href="/corrections">Corrections &amp; Updates</Link> page.
          </p>

          <h2>Contact</h2>
          <p>
            Questions about disclosures can be sent through the{" "}
            <Link href="/contact">contact page</Link> or to{" "}
            <a href="mailto:hello@shariqrefai.com">hello@shariqrefai.com</a>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
